import * as React from 'react'
import * as Api from 'src/api'
import { useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { errorToMessages, type GraduatesFilters } from './query'

const POLL_INTERVAL_MS = 1500

interface ActiveSearch {
  readonly searchId: string
  /** Filters the search was started for; results of an older filter are never shown. */
  readonly filtersKey: string
  readonly initial: Api.AdministratorGraduatesSearch
}

export type GraduatesSearchState =
  | { readonly phase: 'starting' }
  | { readonly phase: 'pending'; readonly progress: number }
  | { readonly phase: 'error'; readonly messages: readonly string[] }
  | {
      readonly phase: 'done'
      readonly rows: Api.getAdministratorStatisticsGraduatesSearchRowsOk | null
      readonly isLoadingRows: boolean
    }

/**
 * The search runs in the background: start → poll progress → read result pages.
 * A result lives 30 minutes on the backend and the same filters get the same searchId back,
 * so paging and repeating a search never recompute it.
 */
export function useGraduatesSearch(
  filters: GraduatesFilters,
  page: number,
  perPage: number
): GraduatesSearchState & { readonly retry: () => void } {
  const headers = useAuthenticatedHeaders()
  const filtersKey = JSON.stringify(filters)
  const [search, setSearch] = React.useState<ActiveSearch | null>(null)
  const [startError, setStartError] = React.useState<{ filtersKey: string; messages: readonly string[] } | null>(null)
  const [attempt, setAttempt] = React.useState(0)
  const requestId = React.useRef(0)
  /** A 404 (expired or unknown search) restarts the search once per filter, then the error is shown. */
  const autoRestartedFor = React.useRef<string | null>(null)

  const latest = React.useRef({ headers, filters })
  latest.current = { headers, filters }

  React.useEffect(() => {
    const id = ++requestId.current
    const { headers, filters } = latest.current
    setStartError(null)
    Api.postAdministratorStatisticsGraduatesSearch({ headers, body: filters }).then(
      (created) => {
        if (id === requestId.current) {
          setSearch({ searchId: created.searchId, filtersKey, initial: created })
        }
      },
      (error: unknown) => {
        if (id === requestId.current) {
          setStartError({ filtersKey, messages: errorToMessages(error) })
        }
      }
    )
  }, [filtersKey, attempt])

  const current = search?.filtersKey === filtersKey ? search : null

  const {
    data: polled,
    error: statusError,
    isValidating: isStatusValidating,
    mutate: mutateStatus,
  } = useApi({
    endpoint: Api.getAdministratorStatisticsGraduatesSearch,
    params: React.useMemo(
      () => ({ headers, args: { searchId: current?.searchId ?? '' } }),
      [headers, current?.searchId]
    ),
    shouldFetch: current != null,
    suspense: false,
    refreshInterval: (data?: Api.AdministratorGraduatesSearch | null) =>
      data == null || data.status === 'pending' ? POLL_INTERVAL_MS : 0,
    refreshWhenHidden: true,
  })

  // The start response is the freshest status; it replaces whatever is cached for that searchId
  React.useEffect(() => {
    if (current != null) {
      void mutateStatus(current.initial, { revalidate: false })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current])

  const status = polled ?? current?.initial ?? null
  const isDone = current != null && status?.status === 'done'

  const {
    data: rows,
    error: rowsError,
    isValidating: isRowsValidating,
  } = useApi({
    endpoint: Api.getAdministratorStatisticsGraduatesSearchRows,
    params: React.useMemo(
      () => ({ headers, args: { searchId: current?.searchId ?? '' }, query: { page, perPage } }),
      [headers, current?.searchId, page, perPage]
    ),
    shouldFetch: isDone,
    suspense: false,
    keepPreviousData: true,
  })

  const restart = React.useCallback(() => {
    void mutateStatus(undefined, { revalidate: false })
    setSearch(null)
    setAttempt((value) => value + 1)
  }, [mutateStatus])

  const retry = React.useCallback(() => {
    autoRestartedFor.current = null
    restart()
  }, [restart])

  React.useEffect(() => {
    if (statusError != null && current != null && autoRestartedFor.current !== current.filtersKey) {
      autoRestartedFor.current = current.filtersKey
      restart()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusError])

  // A failed page usually means the search expired (404) or is not finished (409): the status call tells which
  React.useEffect(() => {
    if (rowsError != null) {
      void mutateStatus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsError])

  let state: GraduatesSearchState
  if (startError?.filtersKey === filtersKey) {
    state = { phase: 'error', messages: startError.messages }
  } else if (current == null || status == null) {
    state = { phase: 'starting' }
  } else if (statusError != null && !isStatusValidating) {
    state = { phase: 'error', messages: errorToMessages(statusError) }
  } else if (status.status === 'failed') {
    state = { phase: 'error', messages: status.message != null && status.message !== '' ? [status.message] : [] }
  } else if (status.status === 'pending') {
    state = { phase: 'pending', progress: Math.min(100, Math.max(0, status.progress ?? 0)) }
  } else if (rowsError != null && !isRowsValidating && !isStatusValidating) {
    state = { phase: 'error', messages: errorToMessages(rowsError) }
  } else {
    state = { phase: 'done', rows: rows ?? null, isLoadingRows: isRowsValidating }
  }

  return { ...state, retry }
}
